import React from "react";
import PropTypes from "prop-types";
import { FormControl, MenuItem, Select } from "@mui/material";
import { styled } from "@mui/material/styles";
import OutlinedInput from "@mui/material/OutlinedInput";
import styles from "./VolumeSelector.module.css";

const VOLUME_RANGE = Array.from({ length: 25 }, (_, index) => -24 + index * 2);
const MIN_VOLUME_DB = -24;
const MAX_VOLUME_DB = 24;

const DARK_BLUE = [21, 101, 192];
const DARK_GRAY = [74, 74, 74];
const DARK_RED = [198, 40, 40];

const interpolateColor = (start, end, t) =>
  start.map((channel, index) =>
    Math.round(channel + (end[index] - channel) * Math.min(Math.max(t, 0), 1))
  );

const getVolumeColor = (value) => {
  if (typeof value !== "number") {
    return "var(--color-dropdown-text)";
  }

  if (value <= 0) {
    const mix = (value - MIN_VOLUME_DB) / (0 - MIN_VOLUME_DB);
    const [r, g, b] = interpolateColor(DARK_BLUE, DARK_GRAY, mix);
    return `rgb(${r}, ${g}, ${b})`;
  }

  const mix = value / MAX_VOLUME_DB;
  const [r, g, b] = interpolateColor(DARK_GRAY, DARK_RED, mix);
  return `rgb(${r}, ${g}, ${b})`;
};

const StyledOutlinedInput = styled(OutlinedInput)(() => ({
  backgroundColor: "var(--color-textfield-bg)",
  borderRadius: "var(--radius-md)",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#bfbfbf",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--color-link-text)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--color-link-text)",
  },
}));

const StyledSelect = styled(Select)(() => ({
  minWidth: 96,
  "& .MuiSelect-select": {
    padding: "8px 10px",
    color: "var(--color-dropdown-text)",
    display: "flex",
    alignItems: "center",
  },
  "& .MuiSelect-icon": {
    color: "var(--color-dropdown-text)",
  },
}));

const VolumeSelector = ({
  value,
  onChange,
  disabled = false,
  className = "",
  placeholder = "🔊",
  name,
  id,
}) => {
  const normalizedValue = typeof value === "undefined" ? "" : value;

  const classNames = [styles.VolumeSelector, className]
    .filter(Boolean)
    .join(" ");

  return (
    <FormControl
      size="small"
      className={classNames}
      disabled={disabled}
      variant="outlined"
    >
      <StyledSelect
        id={id}
        name={name}
        value={normalizedValue}
        onChange={onChange}
        input={<StyledOutlinedInput />}
        displayEmpty
        renderValue={(selected) => {
          if (selected === "" || typeof selected === "undefined") {
            return placeholder;
          }

          if (selected === null) {
            return "Auto";
          }

          const numericValue = Number(selected);

          if (Number.isNaN(numericValue)) {
            return selected;
          }

          if (numericValue === 0) {
            return <span style={{ color: getVolumeColor(numericValue) }}>Raw</span>;
          }

          return (
            <span style={{ color: getVolumeColor(numericValue) }}>{`${numericValue} dB`}</span>
          );
        }}
        MenuProps={{ disableScrollLock: true }}
      >
        <MenuItem value="">{placeholder}</MenuItem>
        <MenuItem value={null}>Auto</MenuItem>
        <MenuItem value={0}>Raw</MenuItem>
        {VOLUME_RANGE.map((option) => (
          <MenuItem
            key={option}
            value={option}
            sx={{ color: `${getVolumeColor(option)} !important` }}
          >
            {option} dB
          </MenuItem>
        ))}
      </StyledSelect>
    </FormControl>
  );
};

VolumeSelector.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.oneOf([null]),
  ]),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  name: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default VolumeSelector;
